import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Ticket, Send, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

export default function VendorTicketPage() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [text, setText] = useState('');
  const [ticketType, setTicketType] = useState('scope');
  const [sending, setSending] = useState(false);

  const load = () => { api.get('/disputes').then(r => setTickets(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [api]);

  const raiseTicket = async () => {
    if (!text.trim()) return;
    setSending(true);
    await api.post('/disputes', { description: text, type: ticketType, project_id: '' });
    setText('');
    load();
    setSending(false);
  };

  return (
    <div className="space-y-5" data-testid="vendor-ticket">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">Raise Ticket</h1>
        <p className="text-xs text-zinc-500 mt-1">For clarification, blocked tasks, technical issues, or payment queries</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">New Ticket</h3>
        <div className="space-y-3">
          <div>
            <label className="text-[9px] text-zinc-500 uppercase">Category</label>
            <div className="flex gap-2 mt-1">
              {[
                { v: 'scope', l: 'Scope / Clarification' },
                { v: 'technical', l: 'Technical Issue' },
                { v: 'payment', l: 'Payment Query' },
                { v: 'deadline', l: 'Deadline Extension' },
                { v: 'quality', l: 'Quality Feedback' },
              ].map(c => (
                <button key={c.v} onClick={() => setTicketType(c.v)}
                  className={`px-3 py-1.5 rounded-sm text-[10px] border ${ticketType === c.v ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' : 'bg-zinc-800/50 border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}>
                  {c.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[9px] text-zinc-500 uppercase">Describe your issue</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="Describe the issue in detail. Include shot numbers, task names, or specific questions..." />
          </div>
          <button onClick={raiseTicket} disabled={sending || !text.trim()}
            className="h-9 px-5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-sm disabled:opacity-40 flex items-center gap-2">
            <Ticket className="w-3.5 h-3.5" /> {sending ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase">My Tickets ({tickets.length})</h3></div>
        <div className="divide-y divide-zinc-800/30">
          {tickets.map(t => (
            <div key={t.id} className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded capitalize">{t.type}</span>
                  <span className={`status-${t.status === 'open' ? 'pending' : t.status === 'resolved' ? 'completed' : 'in_progress'} px-1.5 py-0.5 rounded-full text-[9px]`}>{t.status}</span>
                </div>
                <span className="text-[9px] text-zinc-600">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
              </div>
              <p className="text-xs text-zinc-300 mt-1">{t.description}</p>
              {t.resolution && <p className="text-[10px] text-emerald-400 mt-1">Resolution: {t.resolution}</p>}
            </div>
          ))}
          {tickets.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">No tickets raised yet</p>}
        </div>
      </div>
    </div>
  );
}
